import { useEffect, useState } from 'react';
import { MdEdit, MdCheck, MdClose } from 'react-icons/md';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';

function DetailRow({ label, value }) {
  return (
    <div className="user-detail-row">
      <span className="user-detail-label">{label}</span>
      <span className="user-detail-value">{value}</span>
    </div>
  );
}

function SubscriptionStatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Expired'}
    </span>
  );
}

function UserDetailModal({ user, onClose, onEmailSaved }) {
  const { formatPrice } = usePlatformSettings();
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailNotice, setEmailNotice] = useState('');

  useEffect(() => {
    setEditingEmail(false);
    setEmailDraft(user?.email && user.email !== '—' ? user.email : '');
    setEmailError('');
    setEmailNotice('');
  }, [user]);

  if (!user) return null;

  const subscription = user.subscription || {};
  const features = subscription.features || [];
  const displayEmail = user.email && user.email !== '—' ? user.email : '—';

  const startEmailEdit = () => {
    setEmailDraft(displayEmail === '—' ? '' : displayEmail);
    setEmailError('');
    setEmailNotice('');
    setEditingEmail(true);
  };

  const cancelEmailEdit = () => {
    setEditingEmail(false);
    setEmailDraft(displayEmail === '—' ? '' : displayEmail);
    setEmailError('');
  };

  const saveEmail = async () => {
    const next = String(emailDraft || '').trim().toLowerCase();
    if (!next || !next.includes('@')) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (next === String(displayEmail || '').trim().toLowerCase()) {
      setEditingEmail(false);
      return;
    }
    if (!onEmailSaved) {
      setEmailError('Email save is not available.');
      return;
    }

    try {
      setSavingEmail(true);
      setEmailError('');
      setEmailNotice('');
      await onEmailSaved(user, next);
      setEditingEmail(false);
      setEmailNotice('Email updated.');
    } catch (err) {
      setEmailError(err.message || 'Could not update email.');
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal user-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-detail-title"
      >
        <div className="subscription-modal-header">
          <h2 id="user-detail-title" className="subscription-modal-title">
            User Details
          </h2>
          <button
            type="button"
            className="subscription-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="user-detail-profile">
          <div className="owner-avatar user-detail-avatar">
            {user.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
          </div>
          <div className="user-detail-profile-info">
            <h3 className="user-detail-name">{user.name}</h3>
            {editingEmail ? (
              <div className="user-detail-email-edit">
                <input
                  type="email"
                  className="owner-form-input user-detail-email-input"
                  value={emailDraft}
                  onChange={(e) => {
                    setEmailDraft(e.target.value);
                    setEmailError('');
                  }}
                  placeholder="admin@email.com"
                  autoFocus
                  disabled={savingEmail}
                  aria-label="Admin email"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveEmail();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelEmailEdit();
                    }
                  }}
                />
                <button
                  type="button"
                  className="owner-btn owner-btn-primary owner-btn-sm"
                  onClick={saveEmail}
                  disabled={savingEmail}
                  aria-label="Save email"
                >
                  <MdCheck aria-hidden />
                  {savingEmail ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary owner-btn-sm"
                  onClick={cancelEmailEdit}
                  disabled={savingEmail}
                  aria-label="Cancel email edit"
                >
                  <MdClose aria-hidden />
                  Cancel
                </button>
              </div>
            ) : (
              <div className="user-detail-email-row">
                <p className="user-detail-email">{displayEmail}</p>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary owner-btn-sm user-detail-email-edit-btn"
                  onClick={startEmailEdit}
                  aria-label="Edit email"
                >
                  <MdEdit aria-hidden />
                  Edit
                </button>
              </div>
            )}
            {emailError ? (
              <p className="user-detail-email-msg user-detail-email-msg-error">{emailError}</p>
            ) : null}
            {emailNotice ? (
              <p className="user-detail-email-msg user-detail-email-msg-ok">{emailNotice}</p>
            ) : null}
            <span className={`owner-badge ${user.status === 'active' ? 'owner-badge-success' : 'owner-badge-warning'}`}>
              {user.status === 'active' ? 'Active' : 'Suspended'}
            </span>
          </div>
        </div>

        <div className="user-detail-section">
          <h3 className="user-detail-section-title">Account Information</h3>
          <div className="user-detail-grid">
            <DetailRow label="Gym" value={user.gym} />
            <DetailRow label="Email" value={displayEmail} />
            <DetailRow label="Phone" value={user.phone} />
            <DetailRow label="Joined Date" value={user.joinedDate} />
            <DetailRow label="Account Status" value={user.status === 'active' ? 'Active' : 'Suspended'} />
          </div>
        </div>

        <div className="user-detail-section">
          <h3 className="user-detail-section-title">Subscription Purchased</h3>
          <div className="user-detail-grid">
            <DetailRow label="Plan Name" value={subscription.planName} />
            <DetailRow label="Billing Cycle" value={subscription.billingCycle} />
            <DetailRow label="Price" value={formatPrice(subscription.price)} />
            <DetailRow label="Subscription Status" value={<SubscriptionStatusBadge status={subscription.status} />} />
            <DetailRow label="Start Date" value={subscription.startDate} />
            <DetailRow label="Expiry Date" value={subscription.expiryDate} />
            <DetailRow label="Payment Method" value={subscription.paymentMethod} />
            <DetailRow label="Last Payment" value={subscription.lastPayment} />
            <DetailRow label="Auto Renew" value={subscription.autoRenew ? 'Enabled' : 'Disabled'} />
          </div>
        </div>

        <div className="user-detail-section">
          <h3 className="user-detail-section-title">Plan Features</h3>
          {features.length > 0 ? (
            <ul className="user-feature-list">
              {features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          ) : (
            <p className="user-detail-value">No plan features listed.</p>
          )}
        </div>

        <div className="subscription-form-actions">
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserDetailModal;
